import './NewAdPost.sass';

import React, { FC, useCallback, useRef, useState, ClipboardEvent, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';

import { adsActions } from '$store/ads';
import { MetaTags } from '$shared/components';
import { BottomBar, Button, Header, InfoBlock, ScrollView } from '$uikit';
import { useTranslator } from '$hooks';
import { classNames as cn } from '$utils';
import { goBack } from '$navigation/helper';
import { IcGallery28, IcVideo28 } from '$assets';
import AttachPickerManager from '$shared/components/AttachPickerManager/AttachPickerManager';
import { AttachPickerManagerRef } from '$shared/components/AttachPickerManager/AttachPickerManager.interface';
import { RootState } from '$store/rootReducer';
import { attachItemsSelector } from '$store/attach';
import { toastActions } from '$store/toast';
import { createPostFormSelector, postsActions } from '$store/posts';

export const NewAdPost: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();

  const pickerId = `post_ad_new`;
  const maxLength = 200;
  const form = useSelector((state: RootState) => createPostFormSelector(state, pickerId));
  const [rawText, setRawText] = useState('');
  const editor = useRef<ReactQuill>(null);
  const attachManager = useRef<AttachPickerManagerRef | null>(null);
  const attachments = useSelector((s: RootState) => attachItemsSelector(s, pickerId));

  const [photoInputKey, setPhotoInputKey] = useState(0);
  const handlePhotoChange = useCallback(
    async ({ target: { files } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!files) return;
      attachManager.current?.uploadImages(files);
      setPhotoInputKey(Date.now());
    },
    [attachManager, setPhotoInputKey],
  );

  const [videoInputKey, setVideoInputKey] = useState(0);
  const handleVideoChange = useCallback(
    ({ target }) => {
      if (!target.files.length) return;
      attachManager.current?.uploadVideo(target.files[0]);
      setVideoInputKey(Date.now());
    },
    [attachManager],
  );

  const handleChange = useCallback(
    (newVal) => {
      const contents = editor.current?.getEditor().getContents() ?? '';
      setRawText(editor.current?.getEditor().getText() || '');
      dispatch(
        postsActions.updateFormText({
          formId: pickerId,
          text: JSON.stringify(contents),
        }),
      );
      attachManager.current?.findLinks(newVal);
    },
    [dispatch, attachManager],
  );

  const handlePreview = () => {
    if (rawText.length === 0) {
      return dispatch(toastActions.setToastFail(t('new_ad_post_empty_error')));
    } else if (rawText.length > maxLength) {
      return dispatch(toastActions.setToastFail(t('new_ad_post_limit_error')));
    } else if (attachments.length === 0) {
      return dispatch(toastActions.setToastFail(t('new_ad_post_no_media_error')));
    } else if (attachments === 'photo_uploading') {
      return toastActions.setToastFail('Photo is still loading');
    }
    dispatch(adsActions.createPromoPost());
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const { files } = event.clipboardData;
    if (files[0] && files[0].type.includes('image')) {
      attachManager.current?.uploadImages(files);
    }
  };

  const defaultValue = useMemo(() => {
    if (!form.text) {
      return '';
    }

    if (form.text.substring(0, 1) === '{') {
      try {
        return JSON.parse(form.text);
      } catch (e) {}
    }

    return form.text;
  }, []);

  return (
    <>
      <MetaTags title={t('new_ad_post')} />
      <Header showBackButton backButtonOnClick={() => goBack()}>
        {t('new_ad_post')}
      </Header>
      <ScrollView>
        <div className="NewAdPost" onPaste={handlePaste}>
          <div className="NewAdPost__title">{t('new_ad_post_compose')}</div>
          <InfoBlock type="info">{t('new_ad_post_info')}</InfoBlock>

          <div className="NewAdPost__label">{t('new_ad_post_text_label')}</div>
          {/* @ts-ignore */}
          <ReactQuill
            id="ql_editor"
            bounds=".NewAdPost"
            ref={editor}
            className="Editor"
            theme="bubble"
            placeholder={t('new_post_placeholder')}
            defaultValue={defaultValue}
            formats={['bold', 'italic', 'strike', 'underline', 'link', 'code']}
            onChange={handleChange}
            modules={{
              history: { delay: 1000, maxStack: 100, userOnly: false },
              toolbar: [['bold', 'italic', 'underline', 'strike', 'code']],
            }}
          />
          <div className={cn('NewAdPost__limit', { error: rawText.length > maxLength })}>
            {rawText.length}/{maxLength}
          </div>

          <AttachPickerManager ref={attachManager} pickerId={pickerId} />
          <div className="NewAdPost__attachments">
            <div className="NewAdPost__attachments__photo_wrap">
              <Button mode="secondary" icon={<IcGallery28 />} size="small">
                {t('new_post_attach_photo')}
              </Button>
              <input
                key={photoInputKey}
                onChange={handlePhotoChange}
                multiple
                accept="image/*"
                type="file"
              />
            </div>

            <div className="NewAdPost__attachments__photo_wrap">
              <Button mode="secondary" icon={<IcVideo28 />} size="small">
                {t('new_post_attach_video')}
              </Button>
              <input
                key={videoInputKey}
                type="file"
                onChange={handleVideoChange}
                accept="video/*"
              />
            </div>

            {/* <div className="NewAdPost__attachments__photo_wrap">
              <Button
                size="small"
                mode="secondary"
                icon={<IcCloud28 />}
                onClick={() => {
                  dispatch(
                    cloudActions.setSavedContentActions([
                      // attachActions.pushPhotos({
                      //   pickerId,
                      //   photos: mediaAttachments,
                      // }),
                      attachActions.setTimer({ pickerId, time: timer }),
                      attachActions.setLink({ pickerId, link }),
                      postsActions.updateFormType({
                        formId: pickerId,
                        type: formType,
                      }),
                    ]),
                  );
                  dispatch(
                    cloudActions.setAttachParams({
                      pickerId,
                      routeName: route.name,
                      routeParams: route.params,
                    }),
                  );
                  router.navigate(CLOUD);
                }}
              >
                {t('new_post_attach_cloud')}
              </Button>
            </div> */}
          </div>
        </div>
      </ScrollView>

      <BottomBar>
        <Button onClick={handlePreview} stretched>
          {/* {t('new_ad_post_preview')} */}
          {t('new_ad_post_preview_continue')}
        </Button>
      </BottomBar>
    </>
  );
};
